import React, { useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';

interface Option {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface SelectProps {
  options: Option[];
  value?: string | number;
  defaultValue?: string | number;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  onChange?: (value: string | number, option: Option) => void;
  size?: 'sm' | 'md' | 'lg';
  allowClear?: boolean;
  showSearch?: boolean;
}

const Select: React.FC<SelectProps> = ({
  options,
  value,
  defaultValue,
  placeholder = 'Select an option',
  disabled = false,
  className,
  onChange,
  size = 'md',
  allowClear = false,
  showSearch = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value || defaultValue);
  const [searchTerm, setSearchTerm] = useState('');
  const selectRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base'
  };

  const selectedOption = options.find(option => option.value === selectedValue);
  
  const filteredOptions = showSearch
    ? options.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && showSearch && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen, showSearch]);

  const handleSelect = (option: Option) => {
    if (option.disabled) return;
    
    setSelectedValue(option.value);
    setIsOpen(false);
    setSearchTerm('');
    onChange?.(option.value, option);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedValue(undefined);
    onChange?.('' as any, { value: '', label: '' });
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div ref={selectRef} className={clsx('relative', className)}>
      <div
        className={clsx(
          'flex items-center justify-between w-full border rounded-lg bg-white cursor-pointer transition-colors',
          sizeClasses[size],
          disabled 
            ? 'bg-gray-50 cursor-not-allowed border-gray-200 text-gray-400'
            : isOpen
            ? 'border-blue-500 ring-1 ring-blue-500'
            : 'border-gray-300 hover:border-blue-400'
        )}
        onClick={handleToggle}
      >
        <span className={clsx(
          'block truncate',
          !selectedOption && 'text-gray-400'
        )}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        
        <div className="flex items-center space-x-1">
          {allowClear && selectedOption && !disabled && (
            <button
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          )}
          <ChevronDownIcon 
            className={clsx(
              'h-4 w-4 text-gray-400 transition-transform',
              isOpen && 'transform rotate-180'
            )}
          />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
          {showSearch && (
            <div className="p-2 border-b border-gray-200">
              <input
                ref={searchRef}
                type="text"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          )}
          
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">No options found</div>
          ) : (
            filteredOptions.map((option) => (
              <div
                key={option.value}
                className={clsx(
                  'flex items-center justify-between px-3 py-2 cursor-pointer',
                  option.disabled
                    ? 'text-gray-400 cursor-not-allowed'
                    : selectedValue === option.value
                    ? 'bg-blue-50 text-blue-900'
                    : 'hover:bg-gray-50'
                )}
                onClick={() => handleSelect(option)}
              >
                <span className="block truncate">{option.label}</span>
                {selectedValue === option.value && (
                  <CheckIcon className="h-4 w-4 text-blue-600" />
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Select; 